import type { ProgrammaticLandingPageConfig } from '../../types'
import {
  STORAGE_REFERENCE_KWP,
  SELF_CONSUMPTION_WITHOUT_BATTERY,
  SELF_CONSUMPTION_BATTERY_BONUS,
  SELF_CONSUMPTION_WITH_BATTERY,
  BATTERY_COST_PER_KWH_RANGE_EUR,
} from '../storageSizeFacts'

export const KWH_10_BATERIA_SOLAR: ProgrammaticLandingPageConfig = {
  slug: 'bateria-solar-10-kwh-precio',
  lang: 'es',
  topicId: 'kwh-speicher-10',
  title: 'Batería solar de 10 kWh: precio y valores orientativos 2026',
  metaDescription:
    '¿Cuánto cuesta una batería solar de 10 kWh? Para quién es adecuada, valores orientativos de ' +
    'autoconsumo y comparación gratuita de presupuestos para instalación + batería.',
  keywords: [
    'batería solar 10 kWh precio',
    'acumulador solar 10 kWh coste',
    'batería fotovoltaica 10 kWh',
    'cuánto cuesta una batería de 10 kWh',
  ],
  eyebrow: 'Tamaño de batería: 10 kWh',
  h1: 'Batería solar de 10 kWh: precio y valores orientativos',
  intro:
    `Una batería de 10 kWh es la combinación más habitual con una instalación de ${STORAGE_REFERENCE_KWP} ` +
    'kWp - ambos tamaños siguen la regla práctica habitual de proporción 1:1 kWp:kWh. Aquí ' +
    'encontrará valores orientativos sobre el tamaño de hogar adecuado, el autoconsumo con ' +
    'batería y justo debajo una calculadora con el precio medio ya calculado para esta ' +
    'combinación a partir de presupuestos reales de empresas instaladoras.',
  calculatorPrefill: { kind: 'kwp+storage', kwp: STORAGE_REFERENCE_KWP, kwh: 10 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: '¿Para quién es adecuada una batería de 10 kWh?' },
    {
      type: 'paragraph',
      text:
        `Valor orientativo: 10 kWh se considera la combinación estándar con una instalación de ` +
        `${STORAGE_REFERENCE_KWP} kWp. Con bomba de calor o carga habitual de un coche eléctrico, ` +
        'conviene valorar también 15 o 20 kWh.',
    },
    { type: 'heading', text: 'Autoconsumo con batería solar' },
    {
      type: 'paragraph',
      text:
        `Sin batería, el autoconsumo de una instalación de ${STORAGE_REFERENCE_KWP} kWp ronda ` +
        `normalmente el ${SELF_CONSUMPTION_WITHOUT_BATTERY * 100} %. Con una batería de 10 kWh bien ` +
        `dimensionada, este porcentaje sube en el modelo de cálculo unos ` +
        `${SELF_CONSUMPTION_BATTERY_BONUS * 100} puntos hasta rondar el ` +
        `${SELF_CONSUMPTION_WITH_BATTERY * 100} % - con bomba de calor o coche eléctrico adicional, ` +
        'hasta un límite del 85 %. Un cálculo exacto según su consumo lo ofrece la calculadora de ' +
        'dimensionamiento.',
    },
    { type: 'heading', text: 'Suministro de emergencia' },
    {
      type: 'paragraph',
      text:
        'Una batería de 10 kWh con caja de respaldo suele ofrecer capacidad suficiente para ' +
        'alimentar consumidores centrales durante varias horas o incluso un día completo - el ' +
        'tiempo exacto depende del consumo real de los aparatos conectados. Si un presupuesto ' +
        'incluye o no esta función depende de cada instalador - conviene preguntarlo directamente.',
    },
    { type: 'heading', text: 'Coste de la batería y ayudas' },
    {
      type: 'paragraph',
      text:
        'El coste de las baterías varía mucho según el fabricante y la complejidad de la ' +
        'instalación - la investigación de mercado de 2026 muestra un rango de entre ' +
        `${BATTERY_COST_PER_KWH_RANGE_EUR.min} y ${BATTERY_COST_PER_KWH_RANGE_EUR.max.toLocaleString('es-ES')} ` +
        '€ por kWh. En Alemania existen ayudas específicas para baterías según la región (p. ej. ' +
        'el programa del estado federado de Berlín, con subvención para baterías); en Austria, el ' +
        'sistema EAG añade 150 €/kWh para el acumulador - un cálculo exacto para su combinación lo ' +
        'ofrece la calculadora de abajo.',
    },
  ],
  faq: [
    {
      q: '¿Por qué 10 kWh es la combinación más habitual con una instalación de 10 kWp?',
      a: 'Porque ambos tamaños siguen la regla práctica, muy extendida en el sector, de una proporción 1:1 entre kWp y kWh - un buen punto de partida, aunque no una norma rígida. Una recomendación exacta según su consumo la ofrece la calculadora de dimensionamiento.',
    },
    {
      q: '¿Es suficiente 10 kWh para una vivienda unifamiliar sin bomba de calor ni coche eléctrico?',
      a: 'En la mayoría de los casos sí - 10 kWh se considera el tamaño estándar para viviendas unifamiliares con consumo medio. Con bomba de calor o coche eléctrico, conviene valorar 15 o 20 kWh.',
    },
    {
      q: '¿Debe combinarse siempre una batería de 10 kWh con una instalación de 10 kWp?',
      a: `No, ${STORAGE_REFERENCE_KWP} kWp es aquí solo el tamaño de referencia para mostrar el precio. Con otro tamaño de instalación, la calculadora de dimensionamiento ofrece una recomendación adecuada para su tejado.`,
    },
    {
      q: '¿Es suficiente 10 kWh para ser autosuficiente durante la noche?',
      a: 'En muchos casos sí, incluso con un consumo algo mayor que el de un hogar medio. Una estimación exacta según su consumo real la ofrece la calculadora de dimensionamiento.',
    },
  ],
  primaryCta: { label: 'Solicitar presupuesto gratis ahora', action: 'customer' },
  secondaryCta: {
    label: 'Coste de una instalación fotovoltaica: guía completa',
    action: 'link',
    href: '/coste-instalacion-fotovoltaica',
  },
  relatedLinks: [
    { label: 'Batería solar de 7 kWh: precio y valores orientativos', href: '/bateria-solar-7-kwh-precio' },
    { label: 'Batería solar de 15 kWh: precio y valores orientativos', href: '/bateria-solar-15-kwh-precio' },
    { label: 'Instalación fotovoltaica de 10 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-10-kwp-precio' },
    { label: 'Cómo funciona la comparación de presupuestos', href: '/comparar-presupuestos-fotovoltaicos' },
  ],
}
